/**
 * 텍스트의 공백 포함 글자 수를 계산합니다.
 * @param {string} text - 글자 수를 계산할 텍스트
 * @param {Array} [flexibleData] - 선택: extraSettings/textLength의 data_value
 * @param {string} [fieldName] - 선택: field 값 (예: "subjectComment")
 * @returns {string|Object} - flexibleData 없으면 "123자", 있으면 {text: "123자", color: "red"|"green"|undefined}
 */
export function calculateTextNumber(text, flexibleData, fieldName) {
  if (!text) text = "";
  const length = text.length;

  // 파라미터 없으면 기존처럼 문자열만 반환 (하위 호환성)
  if (!flexibleData || !fieldName) {
    return length + "자";
  }

  // validation 설정 찾기
  const config = flexibleData.find(
    (item) => item.field === fieldName && item.isActive === true
  );

  // 설정 없거나 빈칸이면 일반 텍스트
  if (!config || text.trim() === "") {
    return { text: length + "자", color: undefined };
  }

  const min = parseInt(config.min);
  const max = parseInt(config.max);

  let color = undefined;
  if (length < min || length > max) {
    color = "red";
  } else {
    color = "green";
  }

  return { text: length + "자", color };
}

export function calculateTextNumberByte(text) {
  let content = text;

  // 수학 기호와 그리스 문자를 정의
  const math_symbols = /[+\-*/=<>∞∑∏∫√∂∆πθΩαβγδεζηλμνξοπρστυφχψω·]/g;
  const other_symbols = /[''""]/g;

  // 영어 (1바이트)
  const english = content
    .replace(/[ㄱ-ㅎ|ㅏ-ㅣ|가-힣]/g, "") // 한글 제거
    .replace(/[0-9]/g, "") // 숫자 제거
    .replace(math_symbols, "") // 수학 기호 제거
    .replace(/[{}[\]/?.,;:|)*~`!^_+<>@#$%&\\=()'"-]/g, "") // 특수문자 제거
    .replace(/\s/g, "") // 공백 제거
    .replace(other_symbols, ""); // 기타 기호 제거

  // 한글 (3바이트)
  const korean = content
    .replace(/[a-zA-Z]/g, "") // 영문 제거
    .replace(/[0-9]/g, "") // 숫자 제거
    .replace(math_symbols, "") // 수학 기호 제거
    .replace(/[{}[\]/?.,;:|)*~`!^_+<>@#$%&\\=()'"-]/g, "") // 특수문자 제거
    .replace(/\s/g, "") // 공백 제거
    .replace(other_symbols, ""); // 기타 기호 제거

  // 숫자 (1바이트)
  const number = content
    .replace(/[a-zA-Z]/g, "") // 영문 제거
    .replace(/[ㄱ-ㅎ|ㅏ-ㅣ|가-힣]/g, "") // 한글 제거
    .replace(math_symbols, "") // 수학 기호 제거
    .replace(/[{}[\]/?.,;:|)*~`!^_+<>@#$%&\\=()'"-]/g, "") // 특수문자 제거
    .replace(/\s/g, "") // 공백 제거
    .replace(other_symbols, ""); // 기타 기호 제거

  // 1바이트 특수문자
  const onebyte_special = content
    .replace(/[a-zA-Z]/g, "") // 영문 제거
    .replace(/[ㄱ-ㅎ|ㅏ-ㅣ|가-힣]/g, "") // 한글 제거
    .replace(/[0-9]/g, "") // 숫자 제거
    .replace(/[\n\t\r\s]/g, ""); // 공백 및 줄바꿈 제거

  // 3바이트 특수문자
  const threebyte_special = content
    .replace(/[a-zA-Z]/g, "") // 영문 제거
    .replace(/[ㄱ-ㅎ|ㅏ-ㅣ|가-힣]/g, "") // 한글 제거
    .replace(/[0-9]/g, "") // 숫자 제거
    .replace(math_symbols, "") // 수학 기호 제거
    .replace(/[{}[\]/?.,;:|)*~`!^_+<>@#$%&\\=()'"-]/g, "") // 특수문자 제거
    .replace(/[\n\t\r\s]/g, "") // 공백 및 줄바꿈 제거
    .replace(other_symbols, ""); // 기타 기호 제거

  // 공백 (1바이트)
  const space = content
    .replace(/[a-zA-Z]/g, "") // 영문 제거
    .replace(/[ㄱ-ㅎ|ㅏ-ㅣ|가-힣]/g, "") // 한글 제거
    .replace(/[0-9]/g, "") // 숫자 제거
    .replace(math_symbols, "") // 수학 기호 제거
    .replace(/[{}[\]/?.,;:|)*~`!^_+<>@#$%&\\=()'"-]/g, "") // 특수문자 제거
    .replace(other_symbols, "") // 기타 기호 제거
    .replace(/[^\s]/g, "") // 공백 이외의 문자 제거
    .replace(/[\n\r]/g, ""); // 줄바꿈 문자 제거

  // 줄바꿈 (2바이트)
  const line = content
    .replace(/[a-zA-Z]/g, "") // 영문 제거
    .replace(/[ㄱ-ㅎ|ㅏ-ㅣ|가-힣]/g, "") // 한글 제거
    .replace(/[{}[\]/?.,;:|)*~`!^_+<>@#$%&\\=()'"-]/g, "") // 특수문자 제거
    .replace(/[0-9]/g, "") // 숫자 제거
    .replace(math_symbols, "") // 수학 기호 제거
    .replace(/[^\n]/g, "") // 줄바꿈 이외 문자 제거
    .replace(other_symbols, ""); // 기타 기호 제거

  // 최종 바이트 계산
  const result =
    english.length +
    korean.length * 3 +
    number.length +
    onebyte_special.length +
    threebyte_special.length * 3 +
    space.length +
    line.length * 2;

  return result + "bytes";
}

export function getSchoolYearForSort(schoolYear) {
  if (!schoolYear) return { isNumeric: false, value: "" };

  // console.log("schoolYear", schoolYear);
  // 문자열로 변환
  const yearStr = schoolYear.toString();

  // 첫 번째 4자리 연속 숫자 찾기
  const match = yearStr.match(/\d{4}/);

  if (match) {
    // 숫자를 찾았으면 숫자로 반환
    const year = parseInt(match[0]);

    // console.log("year", year);

    return { isNumeric: true, value: year };
  } else {
    // 4자리 숫자를 못 찾으면 문자열로 반환
    // console.log("yearStr", yearStr);

    return { isNumeric: false, value: yearStr };
  }
}
